import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import useMaskedInput from 'hooks/useMaskedInput';
import { FieldProps } from 'features/Croptimizer/types';
import { TypeOfKey } from 'utils/types';
import CurrentStatusField from 'features/Croptimizer/components/Fields/CurrentStatusField';
import TargetStatusField from 'features/Croptimizer/components/Fields/TargetStatusField';

import Input from '../../../../../../../components/Input';

interface Props {
  disabled: boolean;

  object: FieldProps['object'];
  setFieldObjectField: (
    name: keyof Required<FieldProps>['object']
  ) => (value: TypeOfKey<Required<FieldProps>['object']>) => void;

  currentSec: string;
  currentph: string;
  targetph: string;

  cleancurrentsec: string;
  cleancurrentph: string;
  cleantargetph: string;

  setCurrentSec: (value: string) => void;
  setCurrentPh: (value: string) => void;
  setTargetPh: (value: string) => void;
  isTargetPhChanged: boolean;

  errorCurrent: string;
  errorTarget: string;
  errorTargetSec: string;

  setErrorCurrent: Dispatch<SetStateAction<string>>;
  setErrorTarget: Dispatch<SetStateAction<string>>;
  setErrorTargetSec: Dispatch<SetStateAction<string>>;
}

export const GermanStatusCropRotation: FC<Props> = ({
  disabled,
  object,
  setFieldObjectField,
  targetph,
  currentph,
  currentSec,
  cleancurrentsec,
  cleancurrentph,
  cleantargetph,
  setCurrentSec,
  setErrorCurrent,
  setErrorTarget,
  setErrorTargetSec,
  errorCurrent,
  errorTarget,
  errorTargetSec,
}) => {
  const { t } = useTranslation();

  const [currentSecAuto, setCurrentSecAuto] = useState('');

  const {
    value: targetSec,
    setValue: setTargetSec,
    clean: cleantargetsec,
  } = useMaskedInput({
    defaultValue: object?.targetSecI ?? '',
    value: object?.targetSecI ?? '',
    onChangeCallback: setFieldObjectField('targetSecI'),
  });

  useEffect(() => {
    if (Number(cleancurrentph) < 2.99 && currentph.length > 0) {
      setErrorCurrent(t('minimumPossibleValue') + ' 3');
      setCurrentSec('');

      return;
    }

    if (Number(cleancurrentph) > 8 && currentph.length > 0) {
      setErrorCurrent(t('maximumPossibleValue') + ' 8');
      setCurrentSec('');

      return;
    }

    if (Number(cleancurrentph) < 7.56 && Number(cleancurrentph) > 4.88) {
      setErrorCurrent('');
      const result =
        Math.round(
          (Math.tan((Number(cleancurrentph) - 5.8) / 1.45) * 54 + 70) /
            100 /
            0.05,
        ) * 5;

      setCurrentSec(String(result) + ' %');
      setCurrentSecAuto(String(result) + ' %');

      return;
    }

    if (errorCurrent.length) {
      setErrorCurrent('');
    }
  }, [currentph]);
  useEffect(() => {
    if (
      cleantargetph &&
      Number(cleantargetph) < Number(cleancurrentph) &&
      cleancurrentph
    ) {
      setErrorTarget(
        'La valeur du statut cible ne peut pas être inférieure à la valeur du statut actuel.',
      );

      return;
    }

    if (targetph && Number(cleantargetph) < 2.99) {
      setErrorTarget(t('minimumPossibleValue') + ' 3');
      setTargetSec('');

      return;
    }

    if (targetph && Number(cleantargetph) > 8) {
      setErrorTarget(t('maximumPossibleValue') + ' 8');
      setTargetSec('');

      return;
    }

    if (
      Number(cleantargetph) < 7.56 &&
      Number(cleantargetph) > 4.88 &&
      Number(cleantargetph) >= Number(cleancurrentph) &&
      currentSec !== 'Nicht Relevant'
    ) {
      setErrorTarget('');
      const result =
        Math.round(
          (Math.tan((Number(cleantargetph) - 5.8) / 1.45) * 54 + 70) /
            100 /
            0.05,
        ) * 5;
      setTargetSec(String(result) + ' %');

      return;
    }

    if (errorTarget.length) {
      setErrorTarget('');
    }
  }, [targetph, currentph, currentSec]);
  useEffect(() => {
    if (Number(cleantargetsec) < 0) {
      setErrorTargetSec(t('minimumPossibleValue') + ' is 0');
    }

    if (Number(cleantargetsec) > 300) {
      setErrorTargetSec(t('maximumPossibleValue') + ' is 300');
    }
  }, [targetSec, targetph]);
  useEffect(() => {
    const calcSec = (cleanPh: string, setSec: (value: any) => void) => {
      if (
        (Number(cleanPh) <= 4.88 && Number(cleanPh) >= 3) ||
        (Number(cleanPh) <= 8 && Number(cleanPh) > 7.56)
      ) {
        setSec('Nicht Relevant');
        setCurrentSecAuto('Nicht Relevant');
      }
    };

    calcSec(cleancurrentph, setCurrentSec);
    calcSec(cleantargetph, setTargetSec);
  }, [cleancurrentph, cleantargetph, currentSec]);

  const errorCurrentSecHandle = useMemo(() => {
    if (Number(cleancurrentsec) < 0) {
      return t('minimumPossibleValue') + ' is 0';
    }

    if (Number(cleancurrentsec) > 300) {
      return t('maximumPossibleValue') + ' is 300';
    }

    return '';
  }, [cleancurrentsec]);

  const hasSuffix =
    currentSec.includes('%') || currentSec.startsWith('Nicht Relevant');
  const hasAutoSuffix = currentSecAuto.startsWith('Nicht Relevant');

  const currentSecWithSuffix = hasSuffix
    ? currentSec
    : hasAutoSuffix
      ? currentSecAuto
      : `${currentSec} %`;

  return (
    <div className="flex flex-wrap divide-x divide-primitiveGrey sm:divide-x-0 sm:divide-y mx-[-1rem] sm:mx-0 mt-8">
      <div className="w-1/2 sm:w-full px-4 sm:px-0 sm:pb-4">
        <div className="text-black font-bold text-xl sm:text-lg mb-8">
          {t('currentStatus')}
        </div>
        <div className="my-6">
          <CurrentStatusField isGerman/>
        </div>
        <div className="w-full">
          <Input
            value={currentSecWithSuffix}
            className="w-full h-[48px]"
            titleClassName={`${
              disabled ? 'bg-disable-gradient' : ''
            }  top-[-9px] left-[8px]`}
            title="S/CEC"
            onChange={setCurrentSec}
            disabled={disabled}
            isGreyText={currentSecWithSuffix === currentSecAuto}
            redBorder={errorCurrentSecHandle.length > 0}
          />
          <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
            {errorCurrentSecHandle.length > 0 && (
              <div> {errorCurrentSecHandle}</div>
            )}
          </div>
        </div>
      </div>
      <div className="w-1/2 sm:w-full px-4 sm:px-0 sm:pt-4">
        <div className="text-black font-bold text-xl sm:text-lg mb-8">
          {t('targetStatus')}
          <span
            data-tooltip-id="roi"
            style={{ paddingLeft: 5 }}
            className="font-black text-[#007AC1]"
          >
            ⓘ
          </span>
        </div>
        <Tooltip id="roi" className="tooltip" variant="light" noArrow>
          pH-Wert-Angaben nach VDLUFA-Empfehlung
        </Tooltip>

        <div className="w-full mb-8">
          <TargetStatusField isGerman />
        </div>
        <div className="w-full">
          <Input
            disabled
            value={targetSec}
            className="w-full h-[48px] w-[536px]"
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title="S/CEC"
            isGreyText
          />
          <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
            {errorTargetSec.length > 0 && <div> {errorTargetSec}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
